import { ROUTES } from '@/_constants';
import { AuthorizationHelper, ErrorHandlingHelper, NovatiqPopupHelper } from '@/_helpers';
import { PageSettingService } from '@/features/administration/data/page-setting.service';
import { LanguageService } from '@/_services/language.service';
import { IUserInfo, LoginModel } from '@/_types';
import { NovatiqMessageService } from '@/layouts/service/message.service';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';

@Component({
  selector: 'novatiq-expiry-lock',
  templateUrl: './novatiq-expiry-lock.component.html',
  imports: [ReactiveFormsModule, FormsModule, TranslateModule, InputText, ButtonModule]
})
export class NovatiqExpiryLockComponent implements OnInit, OnDestroy {
  @Input() currentUser: IUserInfo;
  @Output() resumeSession = new EventEmitter<unknown>();

  apiError: string;
  loginForm: FormGroup;
  pageSettings: unknown;

  private readonly authorizationHelper = inject(AuthorizationHelper);
  private readonly formBuilder = inject(FormBuilder);
  private readonly pageSettingService = inject(PageSettingService);
  private readonly popupHelper = inject(NovatiqPopupHelper);
  readonly translate = inject(TranslateService);
  private readonly messageService = inject(NovatiqMessageService);
  private readonly languageService = inject(LanguageService);
  private readonly errorHandlingHelper = inject(ErrorHandlingHelper);

  get formControls() {
    return this.loginForm.controls;
  }

  private get loginModel() {
    return {
      username: this.modelData.username,
      password: this.modelData.password
    } as LoginModel;
  }

  private get modelData() {
    return this.loginForm.value;
  }

  ngOnInit() {
    this.getPageSettings();
    this.initializeProperties();
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      return;
    }

    this.authorizationHelper.logIn(this.loginModel, true).subscribe(
      (authResponse: { userInfo?: { language?: string } }) => {
        const userLocale = authResponse?.userInfo?.language;
        const isDefaultLanguageSelected = Boolean(userLocale);
        if (isDefaultLanguageSelected && userLocale) {
          this.languageService.useLanguage(userLocale);
          this.languageService.setSessionLanguage(userLocale);
        }
        this.resumeSession.emit(authResponse);
        this.loginForm.controls?.password?.setValue('');
        this.popupHelper.close();
      },
      (error) => {
        if (error.errors) {
          const errorMessages = error.errors.map((err) => err.error);
          this.messageService.sendAllErrorMessages(errorMessages);
        } else {
          this.messageService.sendAllErrorMessages([error.message]);
        }
        return this.errorHandlingHelper.handleError(error);
      }
    );
  }

  forgotPassword() {
    this.popupHelper.close();
    this.authorizationHelper.removeUserSessionAndRedirectToForgottenPassword();
  }

  ngOnDestroy() {
    this.loginForm.reset();
  }

  private getPageSettings() {
    this.pageSettingService.get(ROUTES.LOGIN.path).subscribe((settings) => {
      this.pageSettings = settings;
    });
  }

  private initializeProperties() {
    this.apiError = '';
    this.loginForm = this.formBuilder.group({
      username: [this.currentUser?.userName],
      password: ['', Validators.required]
    });
  }
}
